import { DialogTitle } from "@headlessui/react";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import VerificationInput from "react-verification-input";

import { ROUTES } from "@/config/routes";
import { useJwtCreate } from "@/hooks/api/login.hooks";
import { useCheckPinCode } from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { onError } from "@/utils/onError";

import Button from "../buttons/Button";
import Loader from "../helpers/Loader";

const ProfilePinModal = () => {
  const { closeModal } = useModalRouter();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { mutate: checkCode, isPending: checkIsLoading } = useCheckPinCode();
  const [params] = useSearchParams();
  const profileId = params.get("profile_id");

  const {
    mutate,
    isPending: jwtIsLoading,
    status,
  } = useJwtCreate({
    profileId: profileId!,
    age: "18",
  });

  const isLoading = checkIsLoading || jwtIsLoading || status == "success";

  useEffect(() => {
    if (!profileId) {
      closeModal();
    }
  }, [profileId]);

  const handleSubmit = (code: string) => {
    const formData = new FormData();
    formData.append("pin_code", code);
    formData.append("profile_id", profileId!);
    checkCode(
      { formData },
      {
        onSuccess: () => {
          mutate(
            { profile_id: profileId!, pin_code: code },
            {
              onSuccess: () => {
                closeModal();
                navigate(ROUTES.MAIN.MAIN);
              },
              onError,
            },
          );
        },
        onError,
      },
    );
  };

  if (isLoading) return <Loader />;

  return (
    <div className="text-center">
      <DialogTitle as={"h2"} className={"staff-modal__title"}>
        {t("Profile.enter-pin-code")}
      </DialogTitle>
      <p>{t("Profile.enter-pin-code-desc")}</p>
      <div className={"mt-8 flex flex-col items-center"}>
        <VerificationInput
          validChars={"0-9"}
          placeholder=""
          length={4}
          onComplete={handleSubmit}
          classNames={{
            container:
              "h-20 max-lg:w-[13rem] mb-3 w-[16.625rem] flex gap-[0.625rem] max-lg:gap-[0.313rem] responsive-transition",
            character: `bg-surface-container rounded-full h-20 flex items-center justify-center w-[3.75rem] hover:bg-red-1 text-on-primary max-lg:w-[3.125rem] max-lg:h-[4.375rem] responsive-transition outline outline-1 outline-transparent -outline-offset-1`,
            characterSelected:
              "hover:bg-surface-container-high pin-input bg-surface-container-high focus:outline-none border-none outline-on-primary",
            characterFilled: "bg-surface-container-high",
          }}
        />

        <Button className="mt-5" color="white" onClick={closeModal}>
          {t("Actions.cancel")}
        </Button>
      </div>
    </div>
  );
};

export default ProfilePinModal;
